// The person on a LinkedIn or Sales Nav profile (W8, priority 3): enrich, score, push.
//
// Every step is a click. Enrich is free when a Salesforce contact already carries the profile,
// otherwise one Apollo credit. An Apollo hit at a book account is saved to Salesforce and listed
// on the account's Outreach like a Scout find (`onListed` re-reads the book so the list below
// shows them at once); they tick for a sequence once Salesforce saved them. The push is the dashboard's
// Approve card: the server draws it naming the person, the company and the address, and only
// its Approve pushes. The browser never sends an address (`extension/person.py`).

import { useEffect, useState } from 'react';
import { fetchSequences, panelMessage, postEnrichPerson, postPersonPush, postPersonPushCard } from '../api/client';
import type { ExtensionPersonAnswer, Sequence, Viewer } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { ApprovalCard } from '../components/ApprovalCard.ts';
import { parseApprovalCard } from '../lib/approvalCard.ts';
import type { ApprovalCardData, ApprovalDecision } from '../lib/approvalCard.ts';
import type { PageContext } from './bridge.ts';
import { RequestRoute } from './RequestRoute';

const PROFILE_URL_PATTERN = /^https?:\/\/([a-z]{2,3}\.|www\.)?linkedin\.com\/in\/[^/?#]+/i;

function SequencePicker({ onPick, disabled }: { onPick: (sequence: Sequence | null) => void; disabled: boolean }) {
  const { getToken } = useAuthContext();
  const [query, setQuery] = useState('');
  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [chosenId, setChosenId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Debounced search; a slow answer for an older query never lands over a newer one.
  useEffect(() => {
    let abandoned = false;
    const timer = setTimeout(() => {
      fetchSequences(getToken, query).then(payload => {
        if (!abandoned) { setSequences(payload.sequences); setError(null); }
      }).catch(caught => {
        if (!abandoned) setError(panelMessage(caught, 'Could not search sequences.'));
      });
    }, 250);
    return () => { abandoned = true; clearTimeout(timer); };
    // getToken is a stable identity; only the query re-runs the search.
  }, [query]);

  return (
    <div className="extpicker">
      <input type="search" placeholder="Search sequences…" value={query} disabled={disabled}
        onChange={event => setQuery(event.target.value)} aria-label="Search Amplemarket sequences" />
      <select value={chosenId} disabled={disabled} aria-label="Sequence"
        onChange={event => {
          setChosenId(event.target.value);
          onPick(sequences.find(sequence => sequence.id === event.target.value) ?? null);
        }}>
        <option value="">Choose a sequence</option>
        {sequences.map(sequence => <option key={sequence.id} value={sequence.id}>{sequence.name} · {sequence.status}</option>)}
      </select>
      {error ? <p className="extnote bad">{error}</p> : null}
    </div>
  );
}

function HeroLine({ answer }: { answer: ExtensionPersonAnswer }) {
  if (!answer.hero) {
    return <p className="exthint">No hero score: their company is not on the book.</p>;
  }
  const hero = answer.hero;
  const where = answer.account?.name || answer.account?.domain || 'this account';
  return (
    <div className="exthero">
      <p><b>Hero score {Math.round(hero.score)}</b> · #{hero.rank} of {hero.of_count} at {where}
        {hero.is_warm_or_playground ? ' · warm' : ' · cold'}</p>
      <p className="exthint">{hero.because}</p>
      {hero.in_sequence ? <p className="extnote warn">Already in {hero.in_sequence}.</p> : null}
    </div>
  );
}

export function PersonTab({ context, domain, viewer, onListed, onAnswer }: {
  context: PageContext | null;
  domain: string | null;
  viewer: Viewer;
  onListed: () => void;
  // The panel blocks the name-matched account's push when Enrich places them elsewhere.
  onAnswer: (answer: ExtensionPersonAnswer | null) => void;
}) {
  const { getToken } = useAuthContext();
  const pageProfile = context?.profile_url && PROFILE_URL_PATTERN.test(context.profile_url) ? context.profile_url : '';
  const [profileUrl, setProfileUrl] = useState(pageProfile);
  const [answer, setAnswer] = useState<ExtensionPersonAnswer | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sequence, setSequence] = useState<Sequence | null>(null);
  const [card, setCard] = useState<ApprovalCardData | null>(null);
  const [result, setResult] = useState<{ line: string; tone: string } | null>(null);

  // A new page is a new person: forget the last one.
  useEffect(() => {
    setProfileUrl(pageProfile);
    setAnswer(null); setCard(null); setResult(null); setError(null); setSequence(null);
  }, [pageProfile, context?.person_name]);

  // The panel hears every answer (and its reset), so the account below follows the person.
  useEffect(() => { onAnswer(answer); }, [answer]);

  const mayAct = viewer.role === 'admin' || viewer.role === 'strategist';
  // Their company is not on the book: requesting it is the way to push them ("Request it first").
  const requestDomain = answer?.found && !answer.account ? answer.company_domain : null;

  async function enrich() {
    setBusy(true); setError(null); setAnswer(null); setCard(null); setResult(null);
    try {
      const reply = await postEnrichPerson(getToken, {
        linkedin_url: profileUrl.trim(), name: context?.person_name, title: context?.person_title,
        company: context?.person_company, domain: domain ?? undefined,
      });
      setAnswer(reply);

      // Newly saved and listed: re-read the book so the account's Outreach list shows them now.
      if (reply.found && reply.source === 'apollo' && reply.outreach_listed) onListed();
    } catch (caught) {
      setError(panelMessage(caught, 'Enrich failed.'));
    } finally {
      setBusy(false);
    }
  }

  async function drawCard() {
    if (!answer?.person_key || !sequence) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const reply = await postPersonPushCard(getToken, {
        person_key: answer.person_key, target_id: sequence.id, target_name: sequence.name,
      });
      if (!reply.available) { setError(reply.reason || 'No card could be drawn.'); return; }
      setCard(parseApprovalCard(reply.approval));
    } catch (caught) {
      setError(panelMessage(caught, 'Could not draw the card.'));
    } finally {
      setBusy(false);
    }
  }

  async function decide(decision: ApprovalDecision) {
    if (!card) return;
    const cardId = card.request_ids[0];
    if (!cardId) return;
    setBusy(true); setError(null);
    try {
      const reply = await postPersonPush(getToken, { card_id: cardId, decision });
      setCard(null);
      if (decision === 'cancel') { setResult({ line: 'Cancelled. Nothing was pushed.', tone: '' }); return; }
      if (!reply.ok) { setResult({ line: reply.reason || 'Not pushed.', tone: 'bad' }); return; }
      const outcome = (reply.outcome || '').replace('_', ' ');
      setResult({
        line: `“${reply.target_name}” — ${outcome}${reply.detail ? `: ${reply.detail}` : ''}.`,
        tone: reply.outcome === 'added' ? 'ok' : 'warn',
      });
    } catch (caught) {
      setResult({ line: panelMessage(caught, 'The push did not go through.'), tone: 'bad' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="extsection">
      <div className="sblock">
        <h2 className="extcompany">{context?.person_name || 'Person on this page'}</h2>
        {context?.person_title ? <p className="sidesub">{context.person_title}</p> : null}
        {context?.person_company ? <p className="sidesub">{context.person_company}</p> : null}
        {!context?.read_ok ? <p className="extnote warn">Couldn't read this page. Paste their profile address below.</p> : null}
      </div>

      {mayAct ? (
        <div className="sblock">
          <label className="extfield">
            <span>LinkedIn profile</span>
            <input type="url" value={profileUrl} placeholder="https://www.linkedin.com/in/…" disabled={busy}
              onChange={event => setProfileUrl(event.target.value)} />
          </label>
          {context?.kind === 'sales_nav_lead' && !pageProfile
            ? <p className="exthint">Sales Navigator hides the profile address. Open “View LinkedIn profile”, or paste it here.</p>
            : null}
          <div className="extactions">
            <button type="button" className="btn primary" disabled={busy || !PROFILE_URL_PATTERN.test(profileUrl.trim())}
              title="Free if they are in Salesforce; otherwise one Apollo credit."
              onClick={() => { void enrich(); }}>
              Enrich
            </button>
            <span className="exthint">Free in Salesforce · else 1 Apollo credit</span>
          </div>
        </div>
      ) : <p className="extnote">Only an admin or a strategist can enrich and push from here.</p>}

      {error ? <p className="extnote bad">{error}</p> : null}

      {answer && !answer.found ? <p className="extnote warn">{answer.reason}</p> : null}
      {answer && answer.found ? (
        <div className="sblock">
          <p><b>{answer.name}</b>{answer.title ? ` · ${answer.title}` : ''}</p>
          <p className="exthint">
            {answer.email || 'No work email'} · {answer.source === 'salesforce' ? 'their Salesforce contact' : 'found by Apollo'}
            {answer.account ? ` · ${answer.account.name} (${answer.account.owner || 'no owner'})` : ''}
          </p>
          {answer.notes.map(note => <p key={note} className="extnote warn">{note}</p>)}
          {answer.outreach_note
            ? <p className={'extnote ' + (answer.outreach_listed ? 'ok' : 'warn')} role="status">{answer.outreach_note}</p>
            : null}
          <HeroLine answer={answer} />
          {answer.may_push ? (
            <>
              <SequencePicker disabled={busy || card !== null} onPick={setSequence} />
              <div className="extactions">
                <button type="button" className="btn" disabled={busy || !sequence || card !== null}
                  onClick={() => { void drawCard(); }}>
                  Push to sequence…
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="extnote">{answer.push_refusal}</p>
              {requestDomain ? (
                <div className="extactions">
                  <RequestRoute viewer={viewer} domain={requestDomain} name={context?.person_company ?? null}
                    accountId={null} onMyBook={false} excludedWhy={null} />
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {card ? <ApprovalCard card={card} blockedReason={busy ? 'Sending…' : null} onDecide={decision => { void decide(decision); }} /> : null}
      {result ? <p className={'extnote ' + result.tone} role="status">{result.line}</p> : null}
    </div>
  );
}
